import React from "react";
import { useSelector } from "react-redux";
import { Button, SkubotSpinner, Text, colors } from "@commonsku/styles";
import { CatalogItem } from "../../../types";
import { ConvertibleProduct } from "./CatalogUnpublishedView";
import { BASE_ZINDEX } from "../../../popup-factory";
import UpgradeProductPopup from "../../UpgradeProductPopup";
import { useCatalogItemHydration } from "./useCatalogItemHydration";
import {
    CatalogHydrationRootState,
    getCatalogFullItem,
} from "../../../redux/catalog";

interface CatalogUpgradeProductPopupProps {
    item: CatalogItem;
    convertibleProduct: ConvertibleProduct;
    catalogId: string | null;
    onClose: () => void;
    onQueued?: (payload: {
        order_id: string;
        item_id: string;
        item_name: string;
        promo_api_name?: string | null;
        supplier_code?: string | null;
    }) => void;
    onPublishWithoutUpgrade?: () => void;
}

export const CatalogUpgradeProductPopup = ({
    item,
    convertibleProduct,
    catalogId,
    onClose,
    onQueued,
    onPublishWithoutUpgrade,
}: CatalogUpgradeProductPopupProps) => {
    const { ready, item: rawItem, failed } = useCatalogItemHydration(catalogId ?? "", item.item_id, {
        forceRefresh: true,
    });

    const fullItem = useSelector((state: CatalogHydrationRootState) =>
        ready && rawItem
            ? getCatalogFullItem(state, { item_id: item.item_id })
            : null,
    );

    if (!ready || !fullItem) {
        return (
            <div
                onClick={onClose}
                style={{
                    position: "fixed",
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    background: "rgba(0, 0, 0, 0.4)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: BASE_ZINDEX + 2,
                }}
            >
                <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        background: "#fff",
                        borderRadius: 10,
                        padding: 24,
                        minWidth: 320,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 16,
                        color: colors.neutrals[60],
                    }}
                >
                    {failed ? (
                        <>
                            <Text>
                                Failed to load item details. Please try again.
                            </Text>
                            <Button size="medium" onClick={onClose}>
                                Close
                            </Button>
                        </>
                    ) : (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                            }}
                        >
                            <SkubotSpinner
                                size="button"
                                skubot={false}
                                spinnerSize={28}
                                containerSize={28}
                            />
                            <Text>Loading item details…</Text>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <UpgradeProductPopup
            item={fullItem}
            product={convertibleProduct}
            onClose={onClose}
            onPublishWithoutUpgrade={onPublishWithoutUpgrade}
            unavailableFailureMessage={
                onPublishWithoutUpgrade
                    ? "Sorry, upgrade is unavailable at this time. You can publish without the upgrade or try again later."
                    : undefined
            }
            onQueued={({ order_id }: { order_id?: string | null }) =>
                onQueued?.({
                    order_id: order_id ?? catalogId ?? "",
                    item_id: item.item_id,
                    item_name: item.item_name,
                    promo_api_name: item.promo_api_name,
                    supplier_code: item.supplier_code,
                })
            }
        />
    );
};
